import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useSession } from '../session/context';
import {
  clearVideoRef,
  loadVideoRef,
  pickVideoWithHandle,
  reopenFromHandle,
  saveVideoRef,
  supportsFileHandles,
} from './videoStore';
import { StudioVideoContext, type StudioVideoValue } from './studioVideoContext';

/**
 * Provides shared Studio video state (cue-forge-9yc.9). Mounted above the routes
 * so the video survives tab switches, and persists a re-openable reference
 * (handle where supported, else the filename) gated by the autosave preference.
 * Nothing here uploads the video — only a local IndexedDB reference is written.
 */
export function StudioVideoProvider({ children }: { children: ReactNode }) {
  const { autosaveEnabled } = useSession();
  const [videoFile, setFile] = useState<File | null>(null);
  const [restorableName, setRestorableName] = useState<string | null>(null);

  // On mount, surface any video reference a prior session left behind.
  useEffect(() => {
    let active = true;
    void loadVideoRef()
      .then((ref) => {
        if (active && ref) setRestorableName(ref.name);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const persist = useCallback(
    async (name: string, handle?: FileSystemFileHandle) => {
      try {
        if (autosaveEnabled) await saveVideoRef({ name, handle });
        else await clearVideoRef();
      } catch {
        // Best-effort: a persistence failure must never break playback.
      }
    },
    [autosaveEnabled],
  );

  const setVideoFile = useCallback(
    (file: File | null) => {
      setFile(file);
      setRestorableName(null);
      if (file) void persist(file.name);
      else void clearVideoRef().catch(() => {});
    },
    [persist],
  );

  const loadViaPicker = useCallback(async () => {
    const picked = await pickVideoWithHandle();
    if (!picked) return;
    setFile(picked.file);
    setRestorableName(null);
    void persist(picked.file.name, picked.handle);
  }, [persist]);

  const restoreVideo = useCallback(async (): Promise<'ok' | 'repick'> => {
    const ref = await loadVideoRef().catch(() => null);
    if (ref?.handle) {
      const f = await reopenFromHandle(ref.handle);
      if (f) {
        setFile(f);
        setRestorableName(null);
        return 'ok';
      }
    }
    return 'repick';
  }, []);

  const value: StudioVideoValue = {
    videoFile,
    setVideoFile,
    restorableName,
    supportsHandles: supportsFileHandles(),
    loadViaPicker,
    restoreVideo,
  };
  return <StudioVideoContext.Provider value={value}>{children}</StudioVideoContext.Provider>;
}
